import {
	Component,
	effect,
	HostListener,
	OnDestroy,
	OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { fade } from '@core/animations/animations';
import { ICON } from '@core/util/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { select, Select, Store } from '@ngxs/store';
import { UrlService } from '@services/url/url.service';
import { SideNavFlyoutType } from '@state/app/app.model';
import { AppState } from '@state/app/app.state';
import {
	BusinessDispatch,
	BusinessFeatureFlagSelectors,
	BusinessState,
} from '@state/business';
import { LLCMortgageDispatch } from '@state/mortgages/llc-mortgage/llc-mortgage.dispatch';
import { UIBackdropState } from '@state/ui-backdrop.state.ts/ui-backdrop.state';
import { UserProfileDispatch } from '@state/user-profile/dispatch';
import { combineLatest, firstValueFrom, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { envTypes } from 'src/environments/env.types';
import { environment } from 'src/environments/environment';
import { AppMainSelector, IntercomData } from './app-main.selector';
import { UserProfileState } from '@state/user-profile/state';
import { UserBusinessesService } from '@services/user-businesses.service';
import { Logger } from '@core/logger';
import { UserInvitesService } from '@services/user-invites.service';
import { LeapApiService } from '../../shared/api-repos/leap/leap-api.service';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { where } from '@angular/fire/firestore';
import { AuthState } from '@state/auth';
import { BusinessUserStatus } from '@models/businesses';
import { TestHooks } from './app-main.hooks';
import { LoadingModalState } from '@state/global/loading-modal/loading-modal.state';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { ESigningProvider } from '@models/e-signing/enums';

@UntilDestroy()
@Component({
	providers: [UserBusinessesService, UserInvitesService],
	selector: 'app-main',
	templateUrl: './app-main.component.html',
	styleUrls: ['./app-main.component.scss'],
	animations: [fade()],
	standalone: false,
})
export class AppMainComponent implements OnInit, OnDestroy {
	testHooks = TestHooks;
	bottomOffset = 0;
	ICON = ICON;
	legalUrl = UrlService.Legal();
	_grayBg = false;
	_intercomAlive = false;
	public year = new Date().getFullYear();
	public sideNavOptions = SideNavFlyoutType;
	@SelectSnapshot(SelectedMatter.isLeapMatter) isLeapMatter: boolean;
	@Select(BusinessState.getLoaded) loaded$!: Observable<boolean>;
	@Select(AppMainSelector.GetIntercomData)
	intercomData$: Observable<IntercomData>;
	@Select(UIBackdropState.getBackdropState) backdropState$: Observable<boolean>;
	@Select(AppState.sideNavFlyOut) selected$: Observable<string>;
	@Select(AppMainSelector.ShowSideNav) showSideNav$: Observable<boolean>;
	@Select(UserProfileState.getCurrentBusinessId)
	currentBusinessId$!: Observable<string>;
	@Select(LoadingModalState.loading) loading$!: Observable<boolean>;
	isSignItFeatureEnabled = select(
		BusinessFeatureFlagSelectors.sc16163EnableSignIt,
	);
	isSignItSelected = select(BusinessState.isSignITSelected);

	@HostListener('window:beforeunload', ['$event'])
	async unloadHandler() {
		if (this.isLeapMatter) {
			Logger.info(
				`Browser is closing! Calling the backend to send changes to LEAP.`,
			);
			await firstValueFrom(
				this.leapApiService.sendToLeap(
					this.store.selectSnapshot(SelectedMatter.id),
				),
			);
		}
		this.userProfileDispatch.SessionSignOut();
	}

	constructor(
		private llcDispatch: LLCMortgageDispatch,
		private router: Router,
		private userProfileDispatch: UserProfileDispatch,
		private leapApiService: LeapApiService,
		private store: Store,
		private userBusinessesService: UserBusinessesService,
		private userInvitesService: UserInvitesService,
		private businessDispatch: BusinessDispatch,
	) {
		effect(() => {
			if (!this.isSignItFeatureEnabled() && this.isSignItSelected()) {
				this.businessDispatch.OverwriteBusinessFields(
					'defaultSettings.eSigning.defaultProvider',
					ESigningProvider.MANUAL,
					['defaultSettings.eSigning.feeSettings.hasDisconnected', false],
				);
			}
		});
	}

	ngOnDestroy(): void {
		window.Intercom?.('shutdown');
		this._intercomAlive = false;
	}

	ngOnInit(): void {
		console.log('Environment', environment.env);
		Logger.info('Loading App Main Component');
		const loaded = this.loaded$.pipe(
			untilDestroyed(this),
			filter((loaded) => loaded),
		);

		loaded.subscribe(() => {
			Logger.info('llcDispatch.UpdateAll');
			return this.llcDispatch.UpdateAll(false);
		});

		this.currentBusinessId$
			.pipe(
				untilDestroyed(this),
				filter((businessId) => !!businessId),
				distinctUntilChanged(),
			)
			.subscribe((businessId) => {
				console.log('Business Id', businessId);
				if (environment.env === envTypes.DEVELOPMENT) {
					console.log(
						`http://localhost:4001/firestore/data/businesses/${businessId}`,
					);
				}
			});

		loaded
			.pipe(
				switchMap(() =>
					combineLatest([
						this.userBusinessesService.AllFromCollectionGroup(
							where(
								'userUid',
								'==',
								this.store.selectSnapshot(AuthState.getLoggedInUserId),
							),
						),
						this.currentBusinessId$,
					]),
				),
				untilDestroyed(this),
				// Only return BusinessUsers where User is Active in the Business
				map(([businessUsers, currentBusinessId]) =>
					businessUsers
						.filter(
							(businessUser) =>
								businessUser.status === BusinessUserStatus.ACTIVE,
						)
						.map((businessUser) => ({
							name: businessUser.userBusinessName,
							isActive: currentBusinessId === businessUser.userBusinessId,
							businessId: businessUser.userBusinessId,
						}))
						.sort((a) => (a.isActive === true ? -1 : 1)),
				),
			)
			.subscribe((userBusinesses) => {
				this.userBusinessesService.setUserBusinesses(userBusinesses);
			});

		loaded
			.pipe(
				switchMap(() =>
					this.userInvitesService.AllFromCollectionGroup(
						where('email', '==', this.store.selectSnapshot(AuthState.getEmail)),
					),
				),
				untilDestroyed(this),
			)
			.subscribe((invites) => {
				this.userInvitesService.setUserInvites(invites);
			});

		if (!window.Intercom) return;
		this._intercomAlive = true;

		this.intercomData$.pipe(untilDestroyed(this)).subscribe((intercomData) => {
			if (intercomData) {
				window.Intercom('boot', intercomData);
			}
		});

		this.router.events.pipe(untilDestroyed(this)).subscribe(() => {
			this._grayBg = this.router.url.includes('mortgage-inbox');
			if (this._intercomAlive) {
				window.Intercom('update');
			}
		});
	}
}
